import { forwardRefPolymorphic, PolymorphicProps } from "@datamole/wds-std";
import clsx from "clsx";
import { type ForwardedRef, type ElementType } from "react";

import { TypographyColor, TypographyColorShade, TypographyVariant, TypographyWeight } from "./types";
import styles from "./typography.module.css";
import { DEFAULT_COLOR, DEFAULT_COLOR_SHADE, DEFAULT_VARIANT, DEFAULT_WEIGHT } from "./utils";

const DEFAULT_HTML_ELEMENT = "span";

type Props<TAs extends ElementType> = {
    as?: TAs;
    variant?: TypographyVariant;
    color?: TypographyColor;
    colorShade?: TypographyColorShade;
    weight?: TypographyWeight;
} & PolymorphicProps<TAs, typeof DEFAULT_HTML_ELEMENT>;

const TypographyInner = <TAs extends ElementType>(props: Props<TAs>, ref: ForwardedRef<unknown>) => {
    const {
        as,
        children,
        className,
        variant = DEFAULT_VARIANT,
        color = DEFAULT_COLOR,
        colorShade = DEFAULT_COLOR_SHADE,
        weight = DEFAULT_WEIGHT,
        ...otherProps
    } = props;

    const Component = as ?? DEFAULT_HTML_ELEMENT;

    return (
        <Component
            {...otherProps}
            ref={ref}
            className={clsx(styles.typography, className)}
            data-variant={variant}
            data-color={color}
            data-color-shade={colorShade}
            data-weight={weight}
        >
            {children}
        </Component>
    );
};

TypographyInner.displayName = "Typography";

const Typography = forwardRefPolymorphic(TypographyInner);

export { Typography };
export type { Props as TypographyProps };
